import { useCallback, useEffect, useState } from "react";
import { logConnectorFailure, logConnectorSuccess } from "../../../../../telemetry";
import { useDocuparserClient } from "../../../hooks/useDocuparserClient";
import type { DocuparserDocument } from "../../../services/docuparserApi";
import { LIST_ERROR_MESSAGES, PAGE_SIZE } from "../../../constants";

export interface DocumentListErrorState {
  readonly message: string;
}

/**
 * Client-data hook for the document list page, mirroring how MyQueue splits
 * fetch/mutate state into dedicated hooks (see
 * integrations/myqueue/hooks/useMyQueue.ts): owns its own
 * useState/useCallback/useEffect and returns { data, loading, error, actions }
 * — no Redux/Context store, the view stays presentational and just calls the
 * returned actions.
 */
export function useDocuparserDocuments() {
  const client = useDocuparserClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [documents, setDocuments] = useState<DocuparserDocument[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<DocumentListErrorState | null>(null);

  const hasMore = documents.length < totalRecords;

  // `mode: "replace"` is a fresh page-1 load (new search, retry, or first
  // mount) — clears whatever was there before. `mode: "append"` is an
  // infinite-scroll continuation — the new page's results are added to the
  // end of the existing list instead of replacing it.
  const fetchPage = useCallback(
    async (pageToLoad: number, mode: "replace" | "append") => {
      if (mode === "replace") setIsLoading(true);
      else setIsLoadingMore(true);
      setError(null);
      try {
        const response = await client.searchDocuments(activeSearchTerm, pageToLoad, PAGE_SIZE);
        if (response.success && response.data) {
          const data = response.data;
          setDocuments((prev) => (mode === "replace" ? data.results : [...prev, ...data.results]));
          setTotalRecords(data.totalRecords);
          setPage(pageToLoad);
          logConnectorSuccess("docuparser", "search");
        } else {
          setError({ message: response.error || LIST_ERROR_MESSAGES.LOAD_FAILED });
          if (mode === "replace") {
            setDocuments([]);
            setTotalRecords(0);
          }
        }
      } catch (err: unknown) {
        setError({ message: err instanceof Error ? err.message : LIST_ERROR_MESSAGES.RETRY_PROMPT });
        if (mode === "replace") {
          setDocuments([]);
          setTotalRecords(0);
        }
        logConnectorFailure("docuparser", err, "search-failed");
      } finally {
        if (mode === "replace") setIsLoading(false);
        else setIsLoadingMore(false);
      }
    },
    [client, activeSearchTerm],
  );

  const refresh = useCallback(() => fetchPage(1, "replace"), [fetchPage]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    fetchPage(page + 1, "append");
  }, [isLoading, isLoadingMore, hasMore, page, fetchPage]);

  const search = useCallback((term: string) => {
    setActiveSearchTerm(term);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm("");
    setActiveSearchTerm("");
    setError(null);
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    activeSearchTerm,
    search,
    clearSearch,
    documents,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
    refresh,
  };
}
